import { MathUtil } from "@base/math/MathUtil";
import { getKeys, getValueOrFunction, Prim } from "@base/util/TypeUtil";
import { IPropertySelectItem } from "@biz/Definitions";
import { AdditionalArticleInfos, IShoppingArticle } from "@biz/definitions/ArticleDefinitions";
import { ArticleFeatureConfig, ArticleFeatureStore, IArticleFeatureContext, IArticleFeatureHandler, IArticleFeatureInfo, IArticleFeatureTemplate } from "@biz/definitions/ArticleFeatureDefinitions";
import { ArticleFeatureMap } from "@biz/definitions/ArticleFeatureMap";
import { BeosVarianten, mergeBeosVarianten } from "@biz/orders/BeosVarianten";

export class ArticleFeatureLogic {
  /** calculate features that should be preserved because they don't alter the articlenumber (e.g. ropeLength) */
  static calcSaveFeatureStore(featureStore: ArticleFeatureStore | undefined, featureTemplate: IArticleFeatureTemplate | undefined): ArticleFeatureStore | undefined {
    if (!featureTemplate)
      return undefined;

    if (!featureStore)
      return undefined;

    const additionalFeatureStore: ArticleFeatureStore = {};
    let count = 0;
    for (const featureKey of getKeys(featureTemplate)) {
      const template = featureTemplate[featureKey];
      const changesArticleNumber = !template?.mul;
      if ((template?.save || !changesArticleNumber) && !template.readOnly) {
        const f = featureStore[featureKey];
        (additionalFeatureStore as any)[featureKey] = f;
        count++;
      }
    }
    return count === 0 ? undefined : additionalFeatureStore;
  }

  static getMulHandler(featureTemplate: IArticleFeatureTemplate | undefined): (keyof ArticleFeatureStore) | undefined {
    if (!featureTemplate)
      return undefined;

    for (const featureKey of getKeys(featureTemplate)) {
      const template = featureTemplate[featureKey];
      if (template?.mul) {
        return featureKey;
      }
    }
    return undefined;
  }

  static shouldSaveMul(featureTemplate: IArticleFeatureTemplate | undefined): boolean {
    if (!featureTemplate)
      return false;

    for (const featureKey of getKeys(featureTemplate)) {
      const template = featureTemplate[featureKey];
      if (template?.mul && !template.readOnly) {
        return true;
      }
    }
    return false;
  }

  static calcSaveFeatureInfo(featureInfo: IArticleFeatureInfo | undefined, featureTemplate: IArticleFeatureTemplate | undefined): IArticleFeatureInfo | undefined {
    if (!featureTemplate || !featureInfo)
      return undefined;

    return {
      selectedArticle: featureInfo.selectedArticle,
      mul: ArticleFeatureLogic.shouldSaveMul(featureTemplate) ? featureInfo.mul : undefined,
      featureStore: ArticleFeatureLogic.calcSaveFeatureStore(featureInfo.featureStore, featureTemplate)
    }
  }

  static setFeature(featureStore: ArticleFeatureStore, k: string, newVal: Prim, featureArticles: string[] | undefined): ArticleFeatureStore { // test with "GummiSeil"
    const newFeatureStore = ({ ...featureStore, [k]: newVal });
    if (!featureArticles?.length)
      return newFeatureStore;

    { // set newValue in featurestore
      if (ArticleFeatureLogic.findSelectedArticle(newFeatureStore, featureArticles)) {
        return newFeatureStore;
      }
    }
    {
      const newArticle = ArticleFeatureLogic.findSelectedArticle({ [k]: newVal }, featureArticles) || featureArticles?.[0];
      if (newArticle) {
        const ret = { ...newFeatureStore, ...ArticleFeatureMap.getInitialArticleFeatures(newArticle) }; // eg: Gummiseil 00E13000784 keep length
        return ret;
      }
    }
    return newFeatureStore;
  }

  static calc(featureTemplate: IArticleFeatureTemplate | undefined, featureStore: ArticleFeatureStore, featureArticles: string[], context: IArticleFeatureContext): IArticleFeatureInfo | undefined {
    if (!featureTemplate)
      return undefined;

    const selectedArticle = ArticleFeatureLogic.findSelectedArticle(featureStore, featureArticles); // || mainArticleNumber;
    if (!selectedArticle) {
      throw new Error("ArticleFeatureLogic.calc: selectedArticle is undefined");
    }

    let mul = undefined;
    if (selectedArticle) {
      if (featureTemplate) {
        const articleFeatures = ArticleFeatureMap.getInitialArticleFeatures(selectedArticle);
        featureStore = { ...articleFeatures, ...featureStore }; // fill missing config features...
        const ret = ArticleFeatureLogic.#handleFeatureWithMul(featureTemplate, featureStore, context);
        featureStore = ret.featureStore;
        mul = ret.mul;
      }
    }

    return {
      selectedArticle,
      featureStore,
      mul
    };
  }

  static findSelectedArticle(featureStore: ArticleFeatureStore, featureArticles: string[] | undefined): string | undefined {
    if (!featureArticles?.length)
      return undefined;
    for (const articleNumber of featureArticles) {
      const featureConfig = ArticleFeatureMap.getArticleFeatureConfig(articleNumber);
      if (ArticleFeatureLogic.martchesArticleConfig(featureStore, featureConfig || {})) {
        return articleNumber;
      }
    }
    return undefined;
  }

  static matchesArticleFeatures(a: ArticleFeatureStore, articleFeatures: ArticleFeatureStore): boolean {
    for (const k of getKeys(a)) {
      if (k in articleFeatures) {
        if (articleFeatures[k] !== a[k]) {
          return false;
        }
      }
    }
    return true;
  }

  static martchesArticleConfig(a: ArticleFeatureStore, config: ArticleFeatureConfig): boolean {
    for (const k of getKeys(a)) {
      if (k in config) {
        const ck = config[k];
        if (Array.isArray(ck)) {
          if (!(ck as any).includes(a[k]))
            return false;
        } else {
          if (config[k] !== a[k]) {
            return false;
          }
        }
      }
    }
    return true;
  }

  static #handleFeatureWithMul(featureTemplate: IArticleFeatureTemplate, featureStore: ArticleFeatureStore, context: IArticleFeatureContext): { featureStore: ArticleFeatureStore, mul: number | undefined } {
    let mul = undefined;
    const keys = getKeys(featureTemplate)
    for (const k of keys) {
      const featureInfo = featureTemplate[k];
      if (featureInfo?.mul) {
        if (typeof featureInfo.mul === "function") {
          const featureValue = ArticleFeatureLogic.getFeatureValue(featureStore, k, featureTemplate, context);
          const mm = featureInfo.mul(featureValue, featureStore);
          if (mm !== undefined && typeof mm === "number")
            mul = (mul || 1) * mm;
          featureStore = { ...featureStore, [k]: featureValue }
        } else {
          const featureValue = ArticleFeatureLogic.getFeatureValue(featureStore, k, featureTemplate, context);
          if (featureValue !== undefined && typeof featureValue === "number")
            mul = (mul || 1) * featureValue;
          featureStore = { ...featureStore, [k]: featureValue }
        }
      }
    }
    return { featureStore, mul };
  }

  static getAdditionalArticleBeosVarianten(featureTemplate: IArticleFeatureTemplate, article: IShoppingArticle, context: IArticleFeatureContext): BeosVarianten | undefined {
    let beosVarianten: BeosVarianten | undefined;
    for (const featureKey of getKeys(featureTemplate)) {
      const feature = featureTemplate[featureKey];
      if (feature?.beosVariants) {
        const v = ArticleFeatureLogic.getFeatureValue(article?.features || {}, featureKey, featureTemplate, context);
        const bv = feature.beosVariants(v, context, article?.features || {})
        beosVarianten = mergeBeosVarianten(beosVarianten, bv)
      }
    }

    return beosVarianten;
  }

  static getAdditionalArticleInfoItemsForArticle(featureTemplate: IArticleFeatureTemplate, articleNumber: string, articleFeatureStore: ArticleFeatureStore | undefined, context: IArticleFeatureContext): AdditionalArticleInfos {
    const featureStoreValue = ArticleFeatureMap.getInitialArticleFeatures(articleNumber);
    const combined = { ...featureStoreValue, ...articleFeatureStore };
    return ArticleFeatureLogic.getAdditionalArticleInfoItemsForFeatureStore(featureTemplate, combined, context);
  }

  static getAdditionalArticleInfoItemsForFeatureStore(featureTemplate: IArticleFeatureTemplate, featureStore: ArticleFeatureStore, context: IArticleFeatureContext): AdditionalArticleInfos {
    const additionalArticleInfo: AdditionalArticleInfos = {}
    for (const featureKey of getKeys(featureTemplate)) {
      const feature = featureTemplate[featureKey];
      if (feature?.additionalArticleInfo) {
        const v = ArticleFeatureLogic.getFeatureValue(featureStore, featureKey, featureTemplate, context);
        const aInfos = feature.additionalArticleInfo(v, context, featureStore)
        if (aInfos) {
          const infos = Array.isArray(aInfos) ? aInfos : [aInfos];
          for (let i = 0; i < infos.length; i++) {
            const key = Array.isArray(aInfos) ? featureKey + "_" + (i + 1) : featureKey; // https://plaspack.atlassian.net/browse/APP-1569
            additionalArticleInfo[key] = infos[i];
          }
        }
      }
    }

    return additionalArticleInfo
  }

  static getFeatureSelectionItems(feature: IArticleFeatureHandler, featureKey: keyof ArticleFeatureStore, featureArticles: string[] | undefined, context: IArticleFeatureContext): IPropertySelectItem<Prim>[] {
    let possibleFeatureValues: Prim[] | undefined;
    if (!feature.values) {
      possibleFeatureValues = ArticleFeatureLogic.calcPossibleFeatureValues(feature, featureKey, featureArticles);
    } else {
      const values = getValueOrFunction(feature.values, context);
      if (Array.isArray(values)) {
        possibleFeatureValues = values;
      } else if (values.type === "value") {
        possibleFeatureValues = [values.value];
      } else {
        let current = values.min;
        possibleFeatureValues = [];
        while (current <= values.max) {
          possibleFeatureValues.push(current);
          current += values.step || 1;
        }
      }
    }

    return (possibleFeatureValues || []).map((v) => ({ label: feature.formatValue ? feature.formatValue(v, context) : (v ? v.toString() : "" + v), value: v }));
  }

  static calcPossibleFeatureValues(feature: IArticleFeatureHandler, featureKey: keyof ArticleFeatureStore, featureArticles: string[] | undefined): Prim[] | undefined {
    if (!feature.values) {
      if (featureArticles) {
        const items: Prim[] = [];
        for (const fa of featureArticles) {
          const featureConfig = ArticleFeatureMap.getArticleFeatureConfig(fa);
          const afv = featureConfig?.[featureKey];
          const va = Array.isArray(afv) ? afv : [afv];
          for (const v of va) {
            if (items.indexOf(v) === -1)
              items.push(v);
          }
        }
        return feature.sort ? items.sort(feature.sort) : items;
      }
    }
    return undefined;
  }

  static getFeatureValue(obj: ArticleFeatureStore, featureKey: keyof ArticleFeatureStore, featureTemplate: IArticleFeatureTemplate, context: IArticleFeatureContext): Prim | undefined {
    const feature = (featureTemplate as any)[featureKey];
    if (!feature)
      return undefined;

    if (!feature.values) {
      const va = obj?.[featureKey];
      if (Array.isArray(va))
        return va[0]
      return va;
    }

    const storeValue = obj?.[featureKey];
    const values = getValueOrFunction(feature.values, context);
    if (Array.isArray(values)) {
      return storeValue === undefined && values.length > 0 ? (feature.defaultValue !== undefined ? feature.defaultValue : values[0]) : storeValue;
    } else if (values.type === "value") {
      return values.value;
    } else {
      const sv = typeof storeValue === "number" ? storeValue : (feature.defaultValue !== undefined ? feature.defaultValue as number : 0);
      return MathUtil.clamp(sv, values.min, values.max);
    }
  }
}